import { Component, Inject, inject, signal, WritableSignal } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CurrencyService } from 'app/core/services/currency/currency.service';
import { InstanceService } from 'app/core/services/instance/instance.service';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { TranslationsService } from 'app/core/services/translations/translations.service';
import { TimeZoneComponent } from 'app/shared/organisms/time-zone/time-zone';
import { Button } from 'app/shared/atoms/button/button';
import { InputField } from 'app/shared/atoms/input/input';
import { ModalBody } from 'app/shared/molecules/modal/modal-body';
import { ModalFooter } from 'app/shared/molecules/modal/modal-footer';
import { Select } from 'app/shared/atoms/select/select';
import { Textarea } from 'app/shared/atoms/textarea/textarea';
import { NativeModalService } from 'app/shared/services/native-modal.service';
import { countries } from 'app/shared/organisms/contact-communication/data';

/** The languages the app ships catalogs for (src/assets/i18n). */
const APP_LANGUAGES = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
];

/** ISO-2 country codes, upper case as the API stores them. */
const COUNTRY_OPTIONS = countries.map((country) => ({
  value: country.iso.toUpperCase(),
  label: country.name,
}));

/** Where the workspaces created so far operate. */
const DEFAULT_COUNTRY = 'EC';
const DEFAULT_CURRENCY = 'USD';

/**
 * Creates a workspace from the instance selector. Closes with the created
 * instance (the caller enters it) or with nothing when cancelled.
 *
 * The logo is not asked for here: what the cards and the shell show is the
 * instance preference's logo, which is set from the workspace settings once
 * the instance exists.
 */
@Component({
  selector: 'primecontrol-instance-modal',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ModalBody,
    ModalFooter,
    InputField,
    Select,
    Textarea,
    Button,
  ],
  templateUrl: './instance-modal.html',
  styleUrls: ['./instance-modal.scss'],
})
export class InstanceModal {
  public instanceForm: UntypedFormGroup;
  public isSaving: WritableSignal<boolean> = signal(false);
  public languages = APP_LANGUAGES;
  public countries = COUNTRY_OPTIONS;
  /** Starts with the default alone, so the field reads "USD" before the catalog lands. */
  public currencies: WritableSignal<{ value: string; label: string }[]> = signal([
    { value: DEFAULT_CURRENCY, label: DEFAULT_CURRENCY },
  ]);

  private _formBuilder = inject(UntypedFormBuilder);
  private _currencyService = inject(CurrencyService);
  private _instanceService = inject(InstanceService);
  private _notificationService = inject(NotificationService);
  private _translationsService = inject(TranslationsService);
  private _modal = inject(NativeModalService);

  constructor(
    @Inject('CLOSE_DIALOG') private closeModal: (data: any) => void,
    @Inject('TITLE') private assignTitle: (title: string) => void
  ) {
    this.assignTitle('CREATE_INSTANCE');
    this.instanceForm = this._formBuilder.group({
      name: [null, Validators.required],
      description: [''],
      countryIso: [DEFAULT_COUNTRY],
      defaultCurrency: [DEFAULT_CURRENCY],
      defaultLanguage: [this._defaultLanguage(), Validators.required],
      timeZoneReference: [this._browserTimeZone(), Validators.required],
    });
    this._loadCurrencies();
  }

  /** The currency catalog, labelled "USD · US Dollar" so the code reads first. */
  private _loadCurrencies(): void {
    this._currencyService.getCurrencies().subscribe({
      next: (currencies) => {
        const options = (currencies || []).map((currency: any) => ({
          value: currency.currencyIsoCode,
          label: `${currency.currencyIsoCode} · ${currency.currencyName}`,
        }));
        if (options.length) this.currencies.set(options);
      },
      error: (error) => console.error('Could not load the currencies:', error),
    });
  }

  openTimeZone(): void {
    const dialogRef = this._modal.open({
      component: TimeZoneComponent,
    });

    dialogRef.afterClosed().subscribe((result) => {
      const timeZone = this.instanceForm.get('timeZoneReference');
      if (result) {
        timeZone?.setValue(result.name);
        this.instanceForm.markAsDirty();
      }
      // Touched only now that the picker closed (see Select.actionSelect).
      timeZone?.markAsTouched();
    });
  }

  save(): void {
    if (this.instanceForm.invalid || this.isSaving()) {
      this.instanceForm.markAllAsTouched();
      return;
    }

    const { name, description, countryIso, defaultCurrency, defaultLanguage, timeZoneReference } =
      this.instanceForm.value;
    this.isSaving.set(true);
    this._instanceService
      .createInstance({
        name: name.trim(),
        description,
        countryIso,
        defaultCurrency,
        defaultLanguage,
        timeZoneReference,
      })
      .subscribe({
        next: (instance) => {
          this.isSaving.set(false);
          this.closeModal(instance);
        },
        error: (error) => {
          console.error('Error creating instance:', error);
          this.isSaving.set(false);
          this._notificationService
            .notifications({ type: 'error', message: 'INSTANCE_CREATE_ERROR' })
            .subscribe();
        },
      });
  }

  close(): void {
    this.closeModal(null);
  }

  /** The browser's IANA time zone (the picker's catalog is IANA too); empty if it has none. */
  private _browserTimeZone(): string | null {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (error) {
      console.error('Could not read the browser time zone:', error);
      return null;
    }
  }

  /** The UI language when the app has a catalog for it, Spanish otherwise. */
  private _defaultLanguage(): string {
    const current = this._translationsService.getCurrentLanguage();
    return APP_LANGUAGES.some((language) => language.value === current) ? current : 'es';
  }
}
